import React, { useState } from 'react';
import IndividualConversation from '../../../types/IndividualConversation';
import MarkdownMessage from '../MarkdownMessage/MarkdownMessage';
import BotMessagesContainer from '../BotMessagesContainer/BotMessagesContainer';
import formatDate from '../../../utils/formatDate';
import {
  getLanguageName,
  getLanguageCountryCode,
  getFlagUrl
} from '../../../constants/languages';

import MessageRating from '../../../types/MessageRating';
import ReviewTag from '../../../types/ReviewTag';
import Issue from '../../../types/Issue';

export interface MessageReviewUpdate {
  rating?: MessageRating | null;
  tag?: ReviewTag | null;
  comment?: string | null;
  issue?: Issue | null;
}

interface ChatMessageContainerProps {
  conversation: IndividualConversation;
  setError: React.Dispatch<React.SetStateAction<string>>;
  hideReviews?: boolean;
  onMessageReviewUpdate?: (
    messageId: number,
    update: MessageReviewUpdate
  ) => void;
}

function ChatMessageContainer({
  conversation,
  setError,
  hideReviews = false,
  onMessageReviewUpdate
}: ChatMessageContainerProps): React.ReactElement {
  const [translatedState, setTranslatedState] = useState<
    Record<number, boolean>
  >({});

  const getShowTranslated = (index: number): boolean =>
    translatedState[index] ?? true;

  const toggleTranslation = (index: number): void => {
    setTranslatedState((prev) => ({
      ...prev,
      [index]: !getShowTranslated(index)
    }));
  };

  const getUserMessageText = (
    message: (typeof conversation.messageHistory)[0],
    showTranslated: boolean
  ): string => {
    const { textContent, translatedTextContent, sourceLanguage } =
      message.chatMessage;
    const isTranslated = sourceLanguage && sourceLanguage !== 'en';

    if (!isTranslated) {
      return textContent;
    }

    return showTranslated ? translatedTextContent : textContent;
  };

  const isMessageTranslated = (
    message: (typeof conversation.messageHistory)[0]
  ): boolean => {
    const { sourceLanguage } = message.chatMessage;
    return Boolean(sourceLanguage && sourceLanguage !== 'en');
  };

  return (
    <div
      className='[&_ul]:list-inside [&_ol]:list-inside py-0 px-4 rounded flex-1 w-full min-h-[35rem] overflow-x-hidden'
      id='conversation-container'
    >
      {conversation.messageHistory.map((message, index) => {
        const messageIsTranslated = isMessageTranslated(message);
        const { sourceLanguage } = message.chatMessage;
        const showTranslated = getShowTranslated(index);

        return (
          <div key={index} id='message-outer-container'>
            <div className='my-4' id='message-inner-container'>
              <div
                id='user-message'
                className={[
                  '[&_ul]:list-inside [&_ol]:list-inside w-fit px-3 py-2 max-w-[70%]',
                  'rounded-tl rounded-tr rounded-br break-words bg-brand-secondary ml-auto'
                ].join(' ')}
              >
                <MarkdownMessage isUser>
                  {getUserMessageText(message, showTranslated)}
                </MarkdownMessage>
              </div>
              <div className='flex flex-col items-end gap-0.5 mt-2 max-w-full'>
                {messageIsTranslated && sourceLanguage && (
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <img
                      src={getFlagUrl(
                        getLanguageCountryCode(sourceLanguage),
                        20
                      )}
                      alt=''
                      className='w-5 h-4 object-cover rounded-sm flex-shrink-0'
                    />
                    <span className='text-xs text-gray-400'>
                      {showTranslated
                        ? `Translated from ${getLanguageName(sourceLanguage)}`
                        : getLanguageName(sourceLanguage)}
                    </span>
                    <button
                      type='button'
                      onClick={() => toggleTranslation(index)}
                      className='text-xs text-blue-500 hover:text-blue-700 underline'
                    >
                      {showTranslated ? 'View original' : 'View translation'}
                    </button>
                  </div>
                )}
                <p className='text-text-secondary mt-1.5 text-xs text-right'>
                  {formatDate(message.chatMessage.messageTime)}
                  {message.chatMessageResponse.chatMessageResponseBlocks[0]
                    ?.category && (
                    <>
                      {' '}
                      | Topic:{' '}
                      {
                        message.chatMessageResponse.chatMessageResponseBlocks[0]
                          .category
                      }{' '}
                    </>
                  )}
                  | Score:{' '}
                  {message.chatMessage.health === null
                    ? '-'
                    : message.chatMessage.health}
                </p>
              </div>
            </div>
            <div className='my-4' id='bot-message-outer-container'>
              <BotMessagesContainer
                message={message}
                conversation={conversation}
                setError={setError}
                showTranslated={showTranslated}
                hideReviews={hideReviews}
                onMessageReviewUpdate={onMessageReviewUpdate}
              />
            </div>
          </div>
        );
      })}
      <p className='text-center text-white text-sm mt-8'>end of chat</p>
    </div>
  );
}

export default ChatMessageContainer;
